import { useState, useEffect } from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import PayPalButtonBase from "./PayPalButtonBase";
import PromotionalProfileForm from "./PromotionalProfileForm";

export default function PromoPayModal({ onClose, userId: propUserId }) {
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [profileCount, setProfileCount] = useState(1);
  const [processing, setProcessing] = useState(false);
  const [paypalError, setPaypalError] = useState(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [readyToRenderPayPal, setReadyToRenderPayPal] = useState(false);
  const [userId, setUserId] = useState(propUserId || null);
  const [loadingUser, setLoadingUser] = useState(!propUserId);
  const [showProfileForm, setShowProfileForm] = useState(false);

  const clientId = process.env.REACT_APP_PAYPAL_CLIENT_ID;
  const exchangeRate = Number(process.env.REACT_APP_EXCHANGE_RATE) || 60; // DOP per USD

  const plans = [
    {
      id: "basic",
      title: "Plan Básico",
      postsPerMonth: 5,
      priceDOP: 500,
      months: 1,
    },
    {
      id: "premium",
      title: "Plan Premium",
      postsPerMonth: 10,
      priceDOP: 1000,
      months: 1,
    },
  ];

  const getPlanPrice = (plan, profiles) => ({
    usd: Number(((plan.priceDOP / exchangeRate) * profiles).toFixed(2)),
    dop: plan.priceDOP * profiles,
  });

  useEffect(() => {
    if (!propUserId) {
      const auth = getAuth();
      const unsubscribe = onAuthStateChanged(auth, (user) => {
        if (user) {
          setUserId(user.uid);
        } else {
          setUserId(null);
        }
        setLoadingUser(false);
      });
      return () => unsubscribe();
    }
  }, [propUserId]);

  const handleSuccess = () => {
    setProcessing(false);
    setPaymentSuccess(true);
    setReadyToRenderPayPal(false);

    // Calculate centralizedExpirationDate
    const now = new Date();
    const expirationDate = new Date(
      now.setMonth(now.getMonth() + selectedPlan.months)
    );

    setTimeout(() => {
      setPaymentSuccess(false);
      if (profileCount) {
        setShowProfileForm(true);
      }
    }, 2000);
  };

  const handleError = (err) => {
    const message = err?.message || "Error con el pago";
    const details = err?.details ? JSON.stringify(err.details) : "";
    console.error("Payment error: ", message, details);
    setPaypalError(`${message}${details ? ` Detalles: ${details}` : ""}`);
    setProcessing(false);
    setReadyToRenderPayPal(false);
  };

  const handlePaymentClick = () => {
    if (!selectedPlan) {
      setPaypalError("Selecciona un plan válido.");
      return;
    }
    if (profileCount < 1 || profileCount > 3) {
      setPaypalError("Selecciona entre 1 y 3 perfiles.");
      return;
    }
    if (loadingUser) {
      setPaypalError("Cargando usuario, por favor espera...");
      return;
    }
    if (!userId) {
      setPaypalError("Debes iniciar sesión para continuar.");
      return;
    }
    const price = getPlanPrice(selectedPlan, profileCount).usd;
    if (isNaN(price) || price <= 0) {
      setPaypalError("Error en el cálculo del precio.");
      return;
    }
    setProcessing(true);
    setPaypalError(null);
    setReadyToRenderPayPal(true);
  };

  const handleClose = (shouldRefresh = false) => {
    setProcessing(false);
    setReadyToRenderPayPal(false);
    setPaypalError(null);
    setSelectedPlan(null);
    setProfileCount(1);
    setShowProfileForm(false);
    onClose();
    if (shouldRefresh) {
      window.location.reload();
    }
  };

  const handleCloseAndRefresh = () => {
    handleClose(true);
  };

  const handlePlanSelect = (plan) => {
    setSelectedPlan(plan);
    setPaypalError(null);
    setReadyToRenderPayPal(false);
    setProcessing(false);
  };

  const handleProfileCountSelect = (count) => {
    if (count >= 1 && count <= 3) {
      setProfileCount(count);
      setPaypalError(null);
    }
  };

  return (
    <>
      <div
        className="fixed inset-0 bg-gradient-to-br from-black/80 to-indigo-900/60 flex items-center justify-center z-50 p-4 sm:p-6 md:p-8 transition-all duration-500"
        onClick={handleClose}
      >
        <div
          className="bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl p-6 sm:p-8 rounded-3xl shadow-2xl w-full max-w-md sm:max-w-lg md:max-w-2xl max-h-[90vh] overflow-y-auto transform scale-100 hover:scale-[1.03]"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={() => handleClose()}
            className="absolute top-4 right-4 text-gray-400 hover:text-indigo-500 dark:hover:text-indigo-300 text-2xl font-bold transition-transform duration-200 hover:scale-125"
            aria-label="Cerrar modal"
          >
            ×
          </button>

          {paymentSuccess && (
            <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-green-400 to-green-600 text-white px-6 py-4 rounded-xl shadow-lg flex items-center gap-3 animate-bounce z-50">
              <span className="text-2xl">🎉</span>
              <span className="font-bold text-lg">¡Pago completado con éxito!</span>
            </div>
          )}

          <h2 className="text-3xl sm:text-4xl font-extrabold text-center mb-8 bg-clip-text text-transparent bg-gradient-to-r from-indigo-500 to-purple-600 dark:from-indigo-300 dark:to-purple-400">
            Elige tu Plan
          </h2>

          <div className="grid gap-6 sm:grid-cols-2 mb-10">
            {plans.map((plan) => (
              <div
                key={plan.id}
                onClick={() => handlePlanSelect(plan)}
                className={`cursor-pointer p-6 rounded-2xl border-2 transition-all duration-300 transform hover:scale-105 hover:shadow-xl ${
                  selectedPlan?.id === plan.id
                    ? "border-indigo-500 bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/50 dark:to-purple-900/50 shadow-lg"
                    : "border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800"
                }`}
              >
                <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-4">
                  {plan.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300 mb-2">
                  <span className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
                    {plan.postsPerMonth}
                  </span>{" "}
                  posts por mes
                </p>
                <p className="text-gray-600 dark:text-gray-300 mb-2">
                  RD$ <span className="font-bold text-lg">{plan.priceDOP.toFixed(2)}</span> / perfil
                </p>
                <p className="text-gray-600 dark:text-gray-300 mb-2">
                  USD{" "}
                  <span className="font-bold text-lg">${(plan.priceDOP / exchangeRate).toFixed(2)}</span>{" "}
                  / perfil
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-3">
                  Duración: {plan.months} mes
                </p>
              </div>
            ))}
          </div>

          {selectedPlan && (
            <>
              <div className="mb-8">
                <label className="block text-gray-700 dark:text-gray-200 font-semibold text-lg mb-3">
                  Selecciona número de perfiles (1-3):
                </label>
                <div className="flex justify-center gap-4">
                  {[1, 2, 3].map((count) => (
                    <button
                      key={count}
                      onClick={() => handleProfileCountSelect(count)}
                      className={`w-16 h-16 rounded-full text-lg font-bold transition-all duration-300 transform hover:scale-110 ${
                        profileCount === count
                          ? "bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg"
                          : "bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-indigo-100 dark:hover:bg-indigo-900"
                      }`}
                    >
                      {count}
                    </button>
                  ))}
                </div>
              </div>

              <p className="text-center text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-indigo-500 to-purple-600 dark:from-indigo-300 dark:to-purple-400 mb-8">
                Total: ${getPlanPrice(selectedPlan, profileCount).usd.toFixed(2)} USD (RD$
                {getPlanPrice(selectedPlan, profileCount).dop.toFixed(2)})
              </p>

              <div className="text-center mb-8">
                <h2 className="text-lg font-bold bg-clip-text text-transparent bg-gradient-to-r from-indigo-500 to-green-600 dark:from-green-300 dark:to-green-400 mb-3">
                  ¡Beneficio exclusivo para tu primera compra!
                </h2>
                <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                  En tu primera compra solo pagarás el costo del plan que elijas.
                  <br />✅ <strong>Primer mes GRATIS</strong>
                  <br />✅ <strong>Sin costo por perfiles adicionales</strong>
                  <br /><br />
                  Aprovecha esta oportunidad única.
                </p>
              </div>

              {readyToRenderPayPal ? (
                <div className="flex justify-center">
                  <PayPalButtonBase
                    key={`${selectedPlan.id}-${userId}-${profileCount}`}
                    clientId={clientId}
                    createEndpoint="/promo/create-order"
                    captureEndpoint="/promo/capture-order"
                    createBody={{ amount: getPlanPrice(selectedPlan, profileCount).usd, months: selectedPlan.months, userId, planType: selectedPlan.id, profileCount }}
                    captureBody={{ userId, amount: getPlanPrice(selectedPlan, profileCount).usd, planType: selectedPlan.id, profileCount }}
                    onSuccess={handleSuccess}
                    onError={handleError}
                  />
                </div>
              ) : (
                <button
                  onClick={handlePaymentClick}
                  disabled={processing || loadingUser}
                  className="w-full py-4 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-xl font-bold text-lg hover:from-indigo-600 hover:to-purple-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                >
                  {processing ? (
                    <span className="flex items-center justify-center gap-3">
                      <svg className="animate-spin h-6 w-6 text-white" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Procesando...
                    </span>
                  ) : (
                    "Pagar con PayPal / Tarjeta"
                  )}
                </button>
              )}
            </>
          )}

          {paypalError && (
            <p className="text-center text-red-500 font-semibold text-lg mt-6 animate-pulse bg-red-100/50 dark:bg-red-900/50 py-2 rounded-lg">
              {paypalError}
            </p>
          )}

          <button
            onClick={() => handleClose()}
            className="w-full mt-8 text-gray-500 dark:text-gray-400 hover:text-indigo-500 dark:hover:text-indigo-300 transition-colors text-base font-medium"
          >
            Cancelar
          </button>
        </div>
      </div>

      {showProfileForm && selectedPlan && (
        <PromotionalProfileForm
          isOpen={showProfileForm}
          onClose={() => setShowProfileForm(false)}
          onCloseAndRefresh={handleCloseAndRefresh}
          amountUSD={getPlanPrice(selectedPlan, 1).usd}
          centralizedExpirationDate={new Date(
            new Date().setMonth(new Date().getMonth() + selectedPlan.months)
          ).toISOString()}
          planType={selectedPlan.id}
          postsPerMonth={selectedPlan.postsPerMonth}
        />
      )}
    </>
  );
}